import { FormEvent, useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
  TypographyProps,
  IconButton,
  InputAdornment,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import CompanyImage from "../../images/png/LoginPageImage.png";
import type { AppDispatch, RootState } from "../../stores";
import {
  sendOTP,
  verifyOTP,
  resetPassword,
} from "../../features/auth-module/authAction";
import { clearAuthMessages } from "../../features/auth-module/authSlice";

const Container = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  background: "#fff",
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
  transform: "scale(0.97)",
  transformOrigin: "center",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    transform: "scale(0.95)",
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(1.5),
  background: "#fff",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2),
    minHeight: "55vh",
    order: 2,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
    justifyContent: "flex-start",
  },
}));

const ImageSection = styled(Box)(({ theme }) => ({
  flex: 1,
  backgroundSize: "cover",
  backgroundPosition: "center",
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  position: "relative",
  borderRadius: 10,
  transform: "scale(1)",
  [theme.breakpoints.down("md")]: {
    minHeight: "35vh",
    order: 1,
    backgroundPosition: "top center",
    backgroundSize: "120%",
  },
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

const Content = styled(Box)<{ component?: React.ElementType }>(({ theme }) => ({
  width: "100%",
  maxWidth: 450,
  padding: theme.spacing(1.5),
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  marginTop: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.8),
    maxWidth: "100%",
    marginTop: theme.spacing(1.5),
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "5px",
    backgroundColor: "#f5f5f5",
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#bdbdbd",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#4b5b5d",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#757575",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#3f51b5",
  },
  "& .MuiOutlinedInput-input": {
    padding: "10px 12px",
    fontSize: "0.85rem",
    [theme.breakpoints.down("sm")]: {
      padding: "10px 12px",
      fontSize: "0.8rem",
    },
  },
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  fontSize: "28px",
  fontWeight: 700,
  color: "#455354",
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "25px",
  },
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  color: "#6E6E6E",
  fontSize: "12px",
  marginBottom: theme.spacing(2),
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "10px",
    marginBottom: theme.spacing(1.5),
  },
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  color: "#455354",
  fontSize: "0.95rem",
  fontWeight: 600,
  marginBottom: theme.spacing(0.5),
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.8rem",
  },
}));

const SendResetButton = styled(Button)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  width: "100%",
  textTransform: "none",
  fontWeight: 600,
  background: "linear-gradient(to top, #758d8e, #4b5b5d)",
  color: "#fff",
  borderRadius: "6px",
  padding: theme.spacing(1),
  fontSize: "12px",
  "&:hover": {
    background: "linear-gradient(to top, #758d8e, #4b5b5d)",
  },
  "&:disabled": {
    background: "#bdbdbd",
    color: "#fff",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "10px",
    minHeight: "37px",
  },
}));

const OtpTextField = styled(TextField)(({ theme }) => ({
  width: "48px",
  marginRight: theme.spacing(1),
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#f5f5f5",
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#bdbdbd",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#4b5b5d",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#757575",
    fontSize: "0.875rem",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#3f51b5",
  },
  "& .MuiOutlinedInput-input": {
    textAlign: "center",
    padding: "12px 0",
    fontSize: "1rem",
    [theme.breakpoints.down("sm")]: {
      padding: "10px 0",
      fontSize: "0.875rem",
    },
  },
}));

interface BackToLoginLinkProps extends TypographyProps {
  component?: React.ElementType;
}

const BackToLoginLink = styled(Typography)<BackToLoginLinkProps>(
  ({ theme }) => ({
    fontSize: "0.75rem",
    fontWeight: 600,
    textDecoration: "none",
    fontFamily: "'Almarai', sans-serif",
    color: "#455354",
  })
);

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState({
    email: "",
    otp: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [touched, setTouched] = useState({
    email: false,
    otp: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [countdown, setCountdown] = useState(0);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    loading,
    otpSent,
    otpVerified,
    passwordReset,
    error: authError,
  } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    dispatch(clearAuthMessages());
  }, [dispatch]);

  useEffect(() => {
    if (passwordReset) {
      navigate("/login");
    }
  }, [passwordReset, navigate]);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (countdown > 0) {
      interval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [countdown]);

  const validateEmail = (value: string) => {
    if (!value) return "Email Address is required";
    if (value.includes(" ")) return "Email Address cannot contain spaces";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value.trim()))
      return "Enter a valid email address (e.g., user@domain.com)";
    return "";
  };

  const validateOtp = (value: string[]) => {
    const otpString = value.join("");
    if (!otpString) return "OTP is required";
    if (!/^\d{6}$/.test(otpString)) return "OTP must be 6 digits";
    if (otpString.includes(" ")) return "OTP cannot contain spaces";
    return "";
  };

  const validateNewPassword = (value: string) => {
    if (!value) return "New Password is required";
    if (value.includes(" ")) return "New Password cannot contain spaces";
    if (value.length < 8) return "Password must be at least 8 characters";
    return "";
  };

  const validateConfirmPassword = (newPass: string, confirmPass: string) => {
    if (!confirmPass) return "Confirm Password is required";
    if (confirmPass.includes(" "))
      return "Confirm Password cannot contain spaces";
    if (newPass !== confirmPass) return "Passwords do not match";
    return "";
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index?: number
  ) => {
    const { name, value } = e.target;

    if (value.includes(" ")) {
      toast.error(
        `${
          name === "email"
            ? "Email Address"
            : name === "newPassword"
            ? "New Password"
            : name === "confirmPassword"
            ? "Confirm Password"
            : "OTP"
        } cannot contain spaces`
      );
      return;
    }

    if (name === "email") {
      const trimmedValue = value.trim();
      setEmail(trimmedValue);
      if (touched.email) {
        setError((prev) => ({ ...prev, email: validateEmail(trimmedValue) }));
      }
    } else if (name === "newPassword") {
      const trimmedValue = value.trim();
      setNewPassword(trimmedValue);
      if (touched.newPassword) {
        setError((prev) => ({
          ...prev,
          newPassword: validateNewPassword(trimmedValue),
          confirmPassword: validateConfirmPassword(
            trimmedValue,
            confirmPassword
          ),
        }));
      }
    } else if (name === "confirmPassword") {
      const trimmedValue = value.trim();
      setConfirmPassword(trimmedValue);
      if (touched.confirmPassword) {
        setError((prev) => ({
          ...prev,
          confirmPassword: validateConfirmPassword(newPassword, trimmedValue),
        }));
      }
    } else if (typeof index === "number") {
      const newOtp = [...otp];
      const trimmedValue = value.trim().slice(-1);
      if (trimmedValue.includes(" ")) return;
      newOtp[index] = trimmedValue;
      setOtp(newOtp);
      if (touched.otp) {
        setError((prev) => ({ ...prev, otp: validateOtp(newOtp) }));
      }
      if (trimmedValue && index < 5) {
        otpRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedData = e.clipboardData.getData("text").trim();
    if (pastedData.includes(" ")) {
      toast.error("OTP cannot contain spaces");
      return;
    }
    if (/^\d{6}$/.test(pastedData)) {
      const newOtp = pastedData.split("").slice(0, 6);
      setOtp(newOtp);
      if (touched.otp) {
        setError((prev) => ({ ...prev, otp: validateOtp(newOtp) }));
      }
      otpRefs.current[5]?.focus();
    }
    e.preventDefault();
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    if (name === "email") {
      setError((prev) => ({ ...prev, email: validateEmail(email) }));
    } else if (name === "otp") {
      setError((prev) => ({ ...prev, otp: validateOtp(otp) }));
    } else if (name === "newPassword") {
      setError((prev) => ({
        ...prev,
        newPassword: validateNewPassword(newPassword),
        confirmPassword: validateConfirmPassword(newPassword, confirmPassword),
      }));
    } else if (name === "confirmPassword") {
      setError((prev) => ({
        ...prev,
        confirmPassword: validateConfirmPassword(newPassword, confirmPassword),
      }));
    }
  };

  const handleResendOTP = async () => {
    try {
      const result = await dispatch(sendOTP({ email })).unwrap();
      if (result.success) {
        setOtp(["", "", "", "", "", ""]);
        setCountdown(60);
        setError((prev) => ({ ...prev, otp: "" }));
      }
    } catch (err) {
      toast.error(authError || "Failed to resend OTP");
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!otpVerified) {
      setTouched({
        email: true,
        otp: otpSent,
        newPassword: false,
        confirmPassword: false,
      });

      const emailError = validateEmail(email);
      const otpError = otpSent ? validateOtp(otp) : "";

      setError({
        email: emailError,
        otp: otpError,
        newPassword: "",
        confirmPassword: "",
      });

      if (!emailError && (!otpSent || !otpError)) {
        try {
          if (!otpSent) {
            const result = await dispatch(sendOTP({ email })).unwrap();
            if (result.success) {
              setCountdown(60);
              setError({
                email: "",
                otp: "",
                newPassword: "",
                confirmPassword: "",
              });
            }
          } else {
            const otpString = otp.join("");
            const result = await dispatch(
              verifyOTP({ email, otp: otpString })
            ).unwrap();
            if (result.success) {
              setError({
                email: "",
                otp: "",
                newPassword: "",
                confirmPassword: "",
              });
            }
          }
        } catch (err) {
          setError((prev) => ({
            ...prev,
            [otpSent ? "otp" : "email"]:
              authError || "Operation failed. Please try again.",
            newPassword: "",
            confirmPassword: "",
          }));
        }
      }
    } else {
      setTouched({
        email: false,
        otp: false,
        newPassword: true,
        confirmPassword: true,
      });

      const newPasswordError = validateNewPassword(newPassword);
      const confirmPasswordError = validateConfirmPassword(
        newPassword,
        confirmPassword
      );

      setError({
        email: "",
        otp: "",
        newPassword: newPasswordError,
        confirmPassword: confirmPasswordError,
      });

      if (!newPasswordError && !confirmPasswordError) {
        try {
          const result = await dispatch(
            resetPassword({ email, newPassword })
          ).unwrap();
          if (result.success) {
            setError({
              email: "",
              otp: "",
              newPassword: "",
              confirmPassword: "",
            });
          }
        } catch (err) {
          setError((prev) => ({
            ...prev,
            newPassword:
              authError || "Password reset failed. Please try again.",
            confirmPassword: "",
          }));
        }
      }
    }
  };

  const backgroundImageUrl = CompanyImage;

  return (
    <Container>
      <ImageSection sx={{ backgroundImage: `url(${backgroundImageUrl})` }} />
      <FormSection>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Content
            component="form"
            onSubmit={(e: FormEvent) => handleSubmit(e)}
          >
            <WelcomeText>
              {otpVerified ? "Reset Password" : "Reset Your Password"}
            </WelcomeText>
            <Subtitle>
              {otpVerified
                ? "Create a new password for your account."
                : "Enter your email address to receive a OTP to reset password."}
            </Subtitle>

            {!otpVerified ? (
              <>
                <FieldLabel>Email Address</FieldLabel>
                <StyledTextField
                  autoFocus
                  name="email"
                  variant="outlined"
                  type="text"
                  autoComplete="email"
                  value={email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!error.email || !!authError}
                  helperText={error.email || authError}
                  required
                  size={isMobile ? "small" : "medium"}
                />

                {otpSent && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                    }}
                  >
                    <FieldLabel>OTP</FieldLabel>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      {otp.map((digit, index) => (
                        <OtpTextField
                          key={index}
                          name="otp"
                          variant="outlined"
                          type="text"
                          value={digit}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            handleChange(e, index)
                          }
                          onKeyDown={(
                            e: React.KeyboardEvent<HTMLInputElement>
                          ) => handleKeyDown(e, index)}
                          onPaste={index === 0 ? handlePaste : undefined}
                          inputRef={(el) => (otpRefs.current[index] = el)}
                          inputProps={{ maxLength: 1 }}
                          size={isMobile ? "small" : "medium"}
                          error={!!error.otp || !!authError}
                        />
                      ))}
                    </Box>
                    {(error.otp || authError) && (
                      <Typography color="error" variant="caption">
                        {error.otp || authError}
                      </Typography>
                    )}
                    <Box sx={{ mt: 1, textAlign: "center", width: "100%" }}>
                      {countdown > 0 ? (
                        <Typography variant="body2" sx={{color:"#4b5b5d"}}>
                          Resend OTP in {countdown}s
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            color:"#4b5b5d",
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                          onClick={handleResendOTP}
                        >
                          Resend OTP
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </>
            ) : (
              <>
                <FieldLabel>New Password</FieldLabel>
                <StyledTextField
                  autoFocus
                  name="newPassword"
                  variant="outlined"
                  type={showNewPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="At least 8 characters"
                  value={newPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!error.newPassword || !!authError}
                  helperText={error.newPassword || authError}
                  required
                  size={isMobile ? "small" : "medium"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle new password visibility"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                          size={isMobile ? "small" : "medium"}
                        >
                          {showNewPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <FieldLabel>Confirm Password</FieldLabel>
                <StyledTextField
                  name="confirmPassword"
                  variant="outlined"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="At least 8 characters"
                  value={confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!error.confirmPassword}
                  helperText={error.confirmPassword}
                  required
                  size={isMobile ? "small" : "medium"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          edge="end"
                          size={isMobile ? "small" : "medium"}
                        >
                          {showConfirmPassword ? (
                            <Visibility />
                          ) : (
                            <VisibilityOff />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </>
            )}

            <motion.div
              whileHover={{
                scale: 1.01,
                boxShadow: "0 3px 12px rgba(0,0,0,0.08)",
              }}
              whileTap={{ scale: 0.97 }}
              style={{ width: "100%", marginTop: "12px" }}
            >
              <SendResetButton
                type="submit"
                variant="contained"
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                {loading
                  ? otpVerified
                    ? "Resetting..."
                    : otpSent
                    ? "Verifying..."
                    : "Sending..."
                  : otpVerified
                  ? "Reset Password"
                  : otpSent
                  ? "Verify OTP"
                  : "Send OTP To Email"}
              </SendResetButton>
            </motion.div>

            <Box
              sx={{
                width: "100%",
                mt: 2,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <RouterLink to="/login" style={{ textDecoration: "none" }}>
                <BackToLoginLink variant="body2">Back to Login</BackToLoginLink>
              </RouterLink>
            </Box>
          </Content>
        </motion.div>
      </FormSection>
    </Container>
  );
};

export default ForgotPassword;
